import React, { Fragment, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/20/solid';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatCalories } from '../../../utils/calories';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function ItemCard({
  id,
  productItem,
  onClickEditItem,
  onClickDuplicateItem,
  showCalories = true,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: productItem.id });

  const menuBtnRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const updateDropdownPosition = () => {
    if (!menuBtnRef.current) return;
    const rect = menuBtnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setDropdownStyle({
      position: 'fixed',
      zIndex: 9999,
      ...(spaceBelow < 60
        ? {
            bottom: window.innerHeight - rect.top + 4,
            right: window.innerWidth - rect.right,
          }
        : { top: rect.bottom + 4, right: window.innerWidth - rect.right }),
    });
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const calorieLabel = formatCalories(productItem.calories);
  const calorieHidden = productItem.showcalories === false;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      key={id}
      onDoubleClick={() => onClickEditItem(productItem)}
      className={`group transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40 ${isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'}`}
    >
      <td className="px-4 py-4 text-sm font-medium text-zinc-900 dark:text-zinc-200">
        {/* Cap + truncate: an un-clamped name (whitespace-nowrap) grew the table
            until the Actions column scrolled off-screen. The full name stays
            available on hover via title. */}
        <div
          className="max-w-[200px] truncate lg:max-w-[280px]"
          title={productItem.name || '-'}
        >
          {productItem.name || '-'}
        </div>
      </td>
      <td className="whitespace-nowrap py-4 px-4 text-sm text-zinc-900 dark:text-zinc-200">
        {productItem.price.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        }) || '-'}
      </td>
      {showCalories && (
        <td className="whitespace-nowrap py-4 px-4 text-sm text-zinc-500 dark:text-zinc-400">
          {calorieLabel ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={
                  calorieHidden
                    ? 'text-zinc-400 line-through dark:text-zinc-500'
                    : ''
                }
              >
                {calorieLabel}
              </span>
              {calorieHidden && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                  Hidden
                </span>
              )}
            </span>
          ) : (
            '—'
          )}
        </td>
      )}
      <td className="relative whitespace-nowrap py-4 text-right text-sm font-medium sm:pr-6 md:pr-0">
        <div
          className="flex items-center justify-end pr-1"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Menu as="div" className="relative">
            <Menu.Button
              ref={menuBtnRef}
              onClick={updateDropdownPosition}
              className="inline-flex items-center rounded-md p-1 text-slate-300 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </Menu.Button>
            {createPortal(
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  style={dropdownStyle}
                  className="w-36 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-slate-700 dark:ring-slate-600"
                >
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? 'bg-slate-50 dark:bg-slate-600' : '',
                          'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                        )}
                        onClick={() => onClickEditItem(productItem)}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        Edit
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        className={classNames(
                          active ? 'bg-slate-50 dark:bg-slate-600' : '',
                          'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                        )}
                        onClick={() => onClickDuplicateItem(productItem)}
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                        Duplicate
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>,
              document.body,
            )}
          </Menu>
        </div>
      </td>
    </tr>
  );
}

export default ItemCard;
